import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { MapPin, Star, Clock, Users, Camera, Phone, Car, Heart } from "lucide-react";

const ISKCON = () => {
  const navigate = useNavigate();
  const templeImage = "/Indore/temples/iskcon0.jpg";
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "ISKCON Indore",
    "description": "International Society for Krishna Consciousness temple in Indore, dedicated to Lord Krishna with beautiful architecture and spiritual activities",
    "image": templeImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "ISKCON Temple Road",
      "addressLocality": "Indore",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Mo-Su 04:30-21:00",
    "priceRange": "Free",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.7",
      "reviewCount": "7200"
    }
  };

  const highlights = [
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Lord Krishna Temple",
      description: "Beautiful ISKCON temple dedicated to Lord Krishna with stunning architecture and spiritual ambiance"
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: "Spiritual Center",
      description: "International spiritual center offering various devotional activities and cultural programs"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Community Hub",
      description: "Popular destination for spiritual learning, bhajans, and community gatherings"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Cultural Heritage",
      description: "Showcasing traditional Krishna consciousness culture and modern spiritual practices"
    }
  ];

  const visitInfo = [
    { label: "Entry Fee", value: "Free for all visitors" },
    { label: "Timings", value: "4:30 AM - 9:00 PM (All days)" },
    { label: "Aarti Times", value: "Morning 4:30 AM, Evening 7 PM" },
    { label: "Best Time", value: "Morning for mangal aarti" },
    { label: "Duration", value: "1-2 hours" },
    { label: "Facilities", value: "Parking, Prasad, Restaurant, Bookstore" }
  ];

  const festivals = [
    { name: "Janmashtami", time: "August/September", special: "Grand celebrations with special prayers and festivities" },
    { name: "Holi", time: "March", special: "Colorful celebrations and spiritual discourses" },
    { name: "Rath Yatra", time: "June/July", special: "Festival procession and community celebrations" },
    { name: "Gaura Purnima", time: "March", special: "Appearance day celebrations of Lord Chaitanya" }
  ];

  const nearbyAttractions = [
    { name: "Khajrana Ganesh Temple", distance: "3 km", time: "10 mins" },
    { name: "Rajwada Palace", distance: "5 km", time: "15 mins" },
    { name: "Kanch Mandir", distance: "4 km", time: "12 mins" },
    { name: "Lal Bagh Palace", distance: "6 km", time: "18 mins" }
  ];

  return (
    <>
      <Helmet>
        <title>ISKCON Indore - Lord Krishna Temple | Aaoghumen</title>
        <meta name="description" content="Visit ISKCON Indore with Aaoghumen - beautiful temple dedicated to Lord Krishna. Complete guide with timings, festivals, and spiritual activities." />
        <meta name="keywords" content="iskcon indore, iskcon temple indore, lord krishna temple, aaoghumen iskcon, janmashtami indore, spiritual center indore, krishna consciousness" />

        <meta property="og:title" content="ISKCON Indore - Lord Krishna Temple | Aaoghumen" />
        <meta property="og:description" content="Experience divine blessings at ISKCON Indore, dedicated to Lord Krishna with beautiful architecture and spiritual activities." />
        <meta property="og:image" content={templeImage} />
        <meta name="twitter:image" content={templeImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/indore/iskcon" />

        <link rel="canonical" href="https://aaoghumen.com/destinations/indore/iskcon" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-yellow-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-orange-600 via-yellow-500 to-red-600 overflow-hidden">
          <img
            src={templeImage}
            alt="ISKCON Indore"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-orange-300">ISKCON Indore</span>
              </h1>
              <p className="text-xl mb-6">
                Experience the divine presence of Lord Krishna at this beautiful ISKCON temple known for its spiritual activities and architectural splendor.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.7 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>4:30 AM - 9 PM</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>Free Entry</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About ISKCON Indore</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                ISKCON Indore is a magnificent temple dedicated to Lord Krishna, following the International Society for Krishna Consciousness philosophy. The temple is renowned for its stunning architecture, spiritual atmosphere, and various devotional activities that attract thousands of visitors and devotees from around the world.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                The temple features beautiful traditional and modern architectural elements that create a serene and spiritual ambiance. The peaceful environment makes it an ideal place for meditation, prayer, and spiritual learning. ISKCON Indore is known for its community service, educational programs, and cultural activities that promote Krishna consciousness.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, discover the teachings of Krishna consciousness, participate in devotional activities, and experience the temple's vibrant spiritual energy that has made it a beloved destination in Indore.
              </p>
            </div>
          </section>

          {/* Highlights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Temple Highlights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-orange-100 p-3 rounded-lg mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{highlight.title}</h3>
                  </div>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Visit Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Information</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitInfo.map((info, index) => (
                  <div key={index} className="border-l-4 border-orange-500 pl-4">
                    <h3 className="font-bold text-gray-800 mb-2">{info.label}</h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Festivals and Celebrations */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Festivals & Special Occasions</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {festivals.map((festival, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-orange-600 mb-2">{festival.name}</h3>
                  <p className="text-gray-500 mb-3">{festival.time}</p>
                  <p className="text-gray-600">{festival.special}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Spiritual Experiences */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Spiritual Experiences</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-orange-600 mb-4">Mangal Aarti</h3>
                <p className="text-gray-600 mb-4">Join the beautiful morning Mangal Aarti at 4:30 AM and start your day with divine blessings.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• 4:30 AM daily</li>
                  <li>• Traditional ceremonies</li>
                  <li>• Peaceful atmosphere</li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-orange-600 mb-4">Prasad Distribution</h3>
                <p className="text-gray-600 mb-4">Enjoy the blessed vegetarian prasad prepared with love and devotion.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Daily after aarti</li>
                  <li>• Pure vegetarian meals</li>
                  <li>• Blessed preparations</li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-orange-600 mb-4">Bhajan Sessions</h3>
                <p className="text-gray-600 mb-4">Participate in devotional bhajan sessions and experience the joy of Krishna consciousness.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Regular bhajan sessions</li>
                  <li>• Community singing</li>
                  <li>• Spiritual learning</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Nearby Attractions */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nearby Attractions</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nearbyAttractions.map((attraction, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{attraction.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-orange-500" />
                      <span>{attraction.distance}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Car className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{attraction.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How to Reach */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">How to Reach</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Public Transport</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Bus:</strong> City buses to ISKCON Temple area</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Auto:</strong> Available from all parts of city, ₹40-80</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Taxi:</strong> Uber/Ola readily available</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Private Vehicle</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Parking:</strong> Dedicated parking available</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Location:</strong> ISKCON Temple Road, easily accessible</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Navigation:</strong> Search "ISKCON Indore" on maps</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contact and Plan Visit */}
          <section className="bg-gradient-to-r from-orange-600 to-yellow-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Experience Krishna Consciousness</h2>
              <p className="text-xl mb-6">Visit ISKCON Indore with spiritual guidance and cultural insights</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 97139 08893</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>ISKCON Temple Road, Indore</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => navigate('/onboarding')}
                  className="bg-white text-orange-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Plan Temple Visit
                </button>
                <button
                  onClick={() => navigate('/onboarding')}
                  className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-orange-600 transition-colors"
                >
                  Get Directions
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default ISKCON;